import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Lightbulb, User, Users, Shield, FileCheck, Mail, CheckCircle, Building2, Phone, Globe, ArrowLeft, Loader2 } from 'lucide-react';
import type { User as UserType, UserRole } from '../App';
import { register, login, getCurrentUser, requestPasswordReset } from '../api/auth';
import { submitInstitutionApplication } from '../api/institutions';
import { ensureBackendUp } from '../api/backendWake';

interface LoginPageProps {
  onLogin: (user: UserType) => void;
  onBackToLanding?: () => void;
}

type ViewMode = 'select-role' | 'login' | 'register' | 'forgot-password' | 'institution-application' | 'confirmation';

export function LoginPage({ onLogin, onBackToLanding }: LoginPageProps) {
  const [backendReady, setBackendReady] = useState(false);
  const [backendChecking, setBackendChecking] = useState(true);
  const [backendError, setBackendError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await ensureBackendUp();
      if (!cancelled) {
        setBackendChecking(false);
        setBackendReady(ok);
        setBackendError(!ok);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const [viewMode, setViewMode] = useState<ViewMode>('select-role');
  const [selectedRole, setSelectedRole] = useState<UserRole>(null);
  
  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Registration form
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  
  // Institution application form
  const [institutionName, setInstitutionName] = useState('');
  const [institutionType, setInstitutionType] = useState('');
  const [institutionCountry, setInstitutionCountry] = useState('');
  const [institutionWebsite, setInstitutionWebsite] = useState('');
  const [contactPersonName, setContactPersonName] = useState('');
  const [contactPersonEmail, setContactPersonEmail] = useState('');
  const [contactPersonPhone, setContactPersonPhone] = useState('');
  const [contactPersonPosition, setContactPersonPosition] = useState('');
  const [institutionDescription, setInstitutionDescription] = useState('');
  const [innovationCount, setInnovationCount] = useState('');
  
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [applicationReferenceNumber, setApplicationReferenceNumber] = useState<string | null>(null);
  const [institutionApplicationSubmitting, setInstitutionApplicationSubmitting] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState<string | null>(null);
  const [forgotLoading, setForgotLoading] = useState(false);

  const innovationPricingOptions = [
    { range: 'below-10', label: 'Below 10', price: '50,000' },
    { range: '11-50', label: '11-50', price: '100,000' },
    { range: '51-100', label: '51-100', price: '150,000' },
    { range: 'over-100', label: 'Over 100', price: '200,000' }
  ];

  const roles = [
    {
      id: 'innovator' as UserRole,
      title: 'Innovator',
      description: 'Individual entrepreneurs, researchers, and students',
      icon: Lightbulb,
      color: 'text-[var(--gold)]'
    },
    {
      id: 'institution' as UserRole,
      title: 'Institution',
      description: 'Institutions, Companies and Research Organizations',
      icon: Users,
      color: 'text-[var(--gold)]'
    },
    {
      id: 'reviewer' as UserRole,
      title: 'Independent Reviewer',
      description: 'Expert reviewers evaluating innovations',
      icon: FileCheck,
      color: 'text-[var(--gold)]'
    }
    // Admin accounts are created by system administrators only; no self-registration.
  ];

  const handleRoleSelect = (role: UserRole) => {
    setSelectedRole(role);
    if (role === 'institution') {
      setViewMode('institution-application');
    } else {
      setViewMode('register');
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form fields
    if (!registerEmail || !registerEmail.trim()) {
      alert('Please enter an email address');
      return;
    }

    if (!registerPassword || registerPassword.length < 8) {
      alert('Password must be at least 8 characters long');
      return;
    }

    if (registerPassword !== registerConfirmPassword) {
      alert('Passwords do not match');
      return;
    }

    // Check password requirements: at least one uppercase, one lowercase, one digit
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(registerPassword)) {
      alert('Password must contain at least one uppercase letter, one lowercase letter, and one number');
      return;
    }

    if (!selectedRole) {
      alert('Please select a role');
      return;
    }

    // Only innovator and reviewer can self-register; admin is never allowed via API
    let apiRole: 'innovator' | 'reviewer' = selectedRole === 'reviewer' ? 'reviewer' : 'innovator';

    try {
      // Prepare registration data
      const registrationData: any = {
        email: registerEmail.trim(),
        password: registerPassword
      };
      
      // Only include full_name if it's not empty
      if (registerName && registerName.trim()) {
        registrationData.full_name = registerName.trim();
      }
      
      // Only include role if it's valid
      registrationData.role = apiRole;
      
      console.log('Registering with data:', { ...registrationData, password: '***' });
      
      // Call registration API (stores tokens)
      const response = await register(registrationData);

      // Build user from register response; optionally enrich from /users/me (don't fail if 401)
      let name = response.user.full_name || registerName || response.user.email.split('@')[0];
      let institution = response.user.institution_name;
      try {
        const profile = await getCurrentUser();
        if (profile?.user) {
          name = profile.user.full_name || name;
          institution = profile.user.institution_name ?? institution;
        }
      } catch (_) {
        // Use register response only (avoids 401 breaking registration)
      }

      const user: UserType = {
        id: response.user.user_uuid,
        name,
        email: response.user.email,
        role: response.user.role as UserRole,
        institution,
        full_name: response.user.full_name || registerName
      };

      // Show confirmation message
      setConfirmationMessage(`A confirmation email has been sent to ${registerEmail}. Please check your inbox and click the verification link to activate your account.`);
      setViewMode('confirmation');

      // Auto-login after registration
      setTimeout(() => {
        onLogin(user);
      }, 2000);
    } catch (error: any) {
      console.error('Registration error:', error);
      const errorMessage = error.message || 'Registration failed. Please try again.';
      alert(errorMessage);
    }
  };

  const requestedSlotsFromRange: Record<string, number> = {
    'below-10': 10,
    '11-50': 50,
    '51-100': 100,
    'over-100': 200,
  };

  const handleInstitutionApplication = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!institutionName.trim() || !contactPersonName.trim() || !contactPersonEmail.trim() || !innovationCount) {
      alert('Please fill required fields: Institution name, contact person name and email, and innovation count.');
      return;
    }
    setInstitutionApplicationSubmitting(true);
    try {
      const result = await submitInstitutionApplication({
        institution_name: institutionName.trim(),
        short_name: institutionType ? String(institutionType).toUpperCase().slice(0, 20) : undefined,
        official_email: contactPersonEmail.trim(),
        phone_number: contactPersonPhone.trim() || undefined,
        website: institutionWebsite.trim() || undefined,
        physical_address: institutionDescription.trim() || undefined,
        city: undefined,
        country: institutionCountry.trim() || 'Kenya',
        contact_person_name: contactPersonName.trim(),
        contact_person_email: contactPersonEmail.trim(),
        contact_person_phone: contactPersonPhone.trim() || undefined,
        contact_person_title: contactPersonPosition.trim() || undefined,
        requested_slots: requestedSlotsFromRange[innovationCount] ?? 100,
      });
      setApplicationReferenceNumber(result.application.reference_number);
      setConfirmationMessage(
        `Your institution application for "${result.application.institution_name}" has been submitted successfully. Reference: ${result.application.reference_number}. ${result.next_steps}`
      );
      setViewMode('confirmation');
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || 'Application submission failed. Please try again.');
    } finally {
      setInstitutionApplicationSubmitting(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!loginEmail || !loginPassword) {
      alert('Please enter email and password');
      return;
    }

    try {
      // Call login API
      const response = await login({
        email: loginEmail,
        password: loginPassword
      });

      // Fetch full user profile to get all data including name
      const profile = await getCurrentUser();

      // Create user object for app state
      const user: UserType = {
        id: response.user.user_uuid,
        name: profile.user.full_name || response.user.full_name || response.user.email.split('@')[0],
        email: response.user.email,
        role: response.user.role as UserRole,
        institution: response.user.institution_name || profile.user.institution_name,
        full_name: profile.user.full_name || response.user.full_name
      };

      onLogin(user);
    } catch (error: any) {
      alert(error.message || 'Login failed. Please check your credentials.');
    }
  };

  const handleBackToLogin = () => {
    setViewMode('login');
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail || !forgotEmail.trim()) {
      setForgotError('Please enter your email address');
      return;
    }
    setForgotError(null);
    setForgotLoading(true);
    try {
      await requestPasswordReset(forgotEmail.trim());
      setForgotSent(true);
    } catch (err: any) {
      setForgotError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[var(--navy)] flex items-center justify-center p-4" style={{ background: 'var(--navy)' }}>
      <div className="w-full max-w-6xl">
        {/* Logo + tagline in normal flow */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <img src="/images/Corrected%20Logo.png" alt="Insolip" className="h-20 w-auto" />
          </div>
          <p className="text-[var(--text-muted)] max-w-2xl mx-auto font-light">
            Secure Today, Lead Tomorrow.
            <br />
            Evaluate, assess, and protect your intellectual property with precision.
          </p>
        </div>

        {/* Backend connecting: show spinner until health check passes */}
        {backendChecking && (
          <div className="flex flex-col items-center justify-center gap-4 py-12">
            <Loader2 className="h-10 w-10 animate-spin text-[var(--gold)]" />
            <p className="text-white/90">Connecting to service…</p>
          </div>
        )}

        {/* Backend unreachable after timeout */}
        {!backendChecking && backendError && (
          <Card className="max-w-md mx-auto bg-white border-[rgba(201,168,76,0.25)] shadow-xl">
            <CardHeader>
              <CardTitle className="text-[var(--navy)] font-display">Service temporarily unavailable</CardTitle>
              <CardDescription className="text-[var(--navy)]/80">
                The backend did not respond in time. Please try again in a moment.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {onBackToLanding && (
                <Button type="button" variant="outline" className="w-full" onClick={onBackToLanding}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to home
                </Button>
              )}
              <Button
                type="button"
                className="w-full bg-[var(--gold)] text-[var(--navy)] hover:bg-[var(--gold-light)]"
                onClick={() => {
                  setBackendError(false);
                  setBackendChecking(true);
                  ensureBackendUp().then((ok) => {
                    setBackendChecking(false);
                    setBackendReady(ok);
                    setBackendError(!ok);
                  });
                }}
              >
                Try again
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Role Selection and forms: only after backend has responded */}
        {backendReady && viewMode === 'select-role' && (
          <div className="w-full flex flex-col items-center">
            {onBackToLanding && (
              <Button
                type="button"
                variant="ghost"
                className="mb-6 -ml-2 self-start text-white/90 hover:text-[var(--gold)] hover:bg-white/10"
                onClick={onBackToLanding}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to home
              </Button>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl w-full mx-auto">
            {roles.map((role) => {
              const Icon = role.icon;
              return (
                <Card
                  key={role.id}
                  className="cursor-pointer transition-all hover:shadow-lg hover:-translate-y-1 border-2 border-[rgba(201,168,76,0.2)] hover:border-[var(--gold)] bg-[var(--off-white)]"
                  onClick={() => handleRoleSelect(role.id)}
                >
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 mx-auto bg-[var(--gold-pale)] rounded-full flex items-center justify-center mb-4 border border-[rgba(201,168,76,0.2)]">
                      <Icon className={`w-8 h-8 ${role.color}`} />
                    </div>
                    <CardTitle className="text-[var(--navy)] font-display">{role.title}</CardTitle>
                    <CardDescription className="text-center text-[var(--navy)]/70">
                      {role.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button className="w-full bg-[var(--gold)] text-[var(--navy)] hover:bg-[var(--gold-light)] hover:opacity-90">
                      Select Role
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
            </div>
          </div>
        )}

        {/* Login Form (logo sits above this) */}
        {backendReady && viewMode === 'login' && (
          <Card className="max-w-md mx-auto bg-white border-[rgba(201,168,76,0.25)] shadow-xl">
            <CardHeader>
              <CardTitle className="text-[var(--navy)] font-display font-semibold">Sign In as {roles.find(r => r.id === selectedRole)?.title}</CardTitle>
              <CardDescription className="text-[var(--navy)]/80">
                Enter your credentials to access the platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-[var(--navy)]">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password" className="text-[var(--navy)]">Password</Label>
                    <button
                      type="button"
                      onClick={() => { setForgotSent(false); setForgotError(null); setViewMode('forgot-password'); }}
                      className="text-sm text-[var(--gold)] hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setViewMode('select-role');
                      setSelectedRole(null);
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                  >
                    Sign In
                  </Button>
                </div>
                <div className="text-center text-sm text-[var(--navy)]/80">
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setViewMode('register')}
                    className="text-[var(--gold)] font-medium hover:underline"
                  >
                    Create one
                  </button>
                </div>
                <p className="text-xs text-center text-[var(--navy)]/60 mt-2">
                  Administrator accounts are created by system administrators. Contact support if you need admin access.
                </p>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Forgot Password */}
        {backendReady && viewMode === 'forgot-password' && (
          <Card className="max-w-md mx-auto bg-white border-[rgba(201,168,76,0.25)] shadow-xl">
            <CardHeader>
              <CardTitle className="text-[var(--navy)] font-display font-semibold">Reset your password</CardTitle>
              <CardDescription className="text-[var(--navy)]/80">
                Enter the email address for your account and we&apos;ll send you a secure link to reset your password.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {forgotSent ? (
                <div className="space-y-4">
                  <Alert className="bg-[var(--gold-pale)] border-[rgba(201,168,76,0.3)]">
                    <CheckCircle className="h-4 w-4 text-[var(--gold)]" />
                    <AlertDescription className="text-[var(--navy)]">
                      If an account exists with that email, we&apos;ve sent a password reset link. Check your inbox and follow the link to set a new password. The link expires in 1 hour.
                    </AlertDescription>
                  </Alert>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => { setViewMode('login'); setForgotSent(false); setForgotEmail(''); }}
                  >
                    Back to Sign In
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email" className="text-[var(--navy)]">Email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={forgotEmail}
                      onChange={(e) => { setForgotEmail(e.target.value); setForgotError(null); }}
                      required
                    />
                  </div>
                  {forgotError && (
                    <Alert variant="destructive">
                      <AlertDescription>{forgotError}</AlertDescription>
                    </Alert>
                  )}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => { setViewMode('login'); setForgotError(null); setForgotEmail(''); }}
                    >
                      Back
                    </Button>
                    <Button type="submit" className="flex-1" disabled={forgotLoading}>
                      {forgotLoading ? 'Sending…' : 'Send reset link'}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        )}

        {/* Registration Form */}
        {backendReady && viewMode === 'register' && (
          <Card className="max-w-md mx-auto bg-white border-[rgba(201,168,76,0.25)] shadow-xl">
            <CardHeader>
              <CardTitle className="text-[var(--navy)] font-display font-semibold">Create Account - {roles.find(r => r.id === selectedRole)?.title}</CardTitle>
              <CardDescription className="text-[var(--navy)]/80">
                Fill in your details to create your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="register-name" className="text-[var(--navy)]">Full Name</Label>
                  <Input
                    id="register-name"
                    type="text"
                    placeholder="John Doe"
                    value={registerName}
                    onChange={(e) => setRegisterName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-email" className="text-[var(--navy)]">Email</Label>
                  <Input
                    id="register-email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={registerEmail}
                    onChange={(e) => setRegisterEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-password" className="text-[var(--navy)]">Password</Label>
                  <Input
                    id="register-password"
                    type="password"
                    placeholder="••••••••"
                    value={registerPassword}
                    onChange={(e) => setRegisterPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-confirm-password" className="text-[var(--navy)]">Confirm Password</Label>
                  <Input
                    id="register-confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={registerConfirmPassword}
                    onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setViewMode('select-role');
                      setSelectedRole(null);
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                  >
                    Create Account
                  </Button>
                </div>
                <div className="text-center text-sm text-[var(--navy)]/80">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setViewMode('login')}
                    className="text-[var(--gold)] font-medium hover:underline"
                  >
                    Sign in
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Institution Application Form */}
        {backendReady && viewMode === 'institution-application' && (
          <Card className="max-w-3xl mx-auto bg-white border-[rgba(201,168,76,0.25)] shadow-xl">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="w-8 h-8 text-[var(--gold)]" />
                <div>
                  <CardTitle className="text-[var(--navy)] font-display font-semibold">Institution Application</CardTitle>
                  <CardDescription className="text-[var(--navy)]/80">
                    Submit your institution details for administrative review
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Alert className="mb-6 bg-[var(--gold-pale)] border-[rgba(201,168,76,0.3)]">
                <Mail className="h-4 w-4 text-[var(--gold)]" />
                <AlertDescription className="text-[var(--navy)]">
                  Your application will be reviewed by our administrative team within 3-5 business days. You will receive an email confirmation once your access has been granted.
                </AlertDescription>
              </Alert>

              <form onSubmit={handleInstitutionApplication} className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-[var(--navy)] font-semibold">Institution Information</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="institution-name" className="text-[var(--navy)]">Institution Name *</Label>
                    <Input
                      id="institution-name"
                      type="text"
                      placeholder="e.g., University of Technology"
                      value={institutionName}
                      onChange={(e) => setInstitutionName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="institution-type" className="text-[var(--navy)]">Institution Type *</Label>
                      <Select value={institutionType} onValueChange={setInstitutionType} required>
                        <SelectTrigger id="institution-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="university">University</SelectItem>
                          <SelectItem value="research-institute">Research Institute</SelectItem>
                          <SelectItem value="college">College</SelectItem>
                          <SelectItem value="polytechnic">Polytechnic</SelectItem>
                          <SelectItem value="innovation-hub">Innovation Hub</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="institution-country" className="text-[var(--navy)]">Country *</Label>
                      <Input
                        id="institution-country"
                        type="text"
                        placeholder="e.g., Kenya"
                        value={institutionCountry}
                        onChange={(e) => setInstitutionCountry(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="institution-website" className="text-[var(--navy)]">Institution Website</Label>
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <Input
                        id="institution-website"
                        type="url"
                        placeholder="https://www.institution.edu"
                        value={institutionWebsite}
                        onChange={(e) => setInstitutionWebsite(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="institution-description" className="text-[var(--navy)]">Institution Description</Label>
                    <Textarea
                      id="institution-description"
                      rows={3}
                      placeholder="Brief description of your institution and innovation activities..."
                      value={institutionDescription}
                      onChange={(e) => setInstitutionDescription(e.target.value)}
                    />
                  </div>
                </div>

                <div className="border-t pt-6 space-y-4">
                  <h3 className="text-[var(--navy)] font-semibold">Contact Person</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact-person-name" className="text-[var(--navy)]">Full Name *</Label>
                      <Input
                        id="contact-person-name"
                        type="text"
                        placeholder="e.g., Dr. Jane Kamau"
                        value={contactPersonName}
                        onChange={(e) => setContactPersonName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-person-email" className="text-[var(--navy)]">Email *</Label>
                      <Input
                        id="contact-person-email"
                        type="email"
                        placeholder="jane.kamau@institution.edu"
                        value={contactPersonEmail}
                        onChange={(e) => setContactPersonEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-person-phone" className="text-[var(--navy)]">Phone</Label>
                      <Input
                        id="contact-person-phone"
                        type="tel"
                        placeholder="+254 XXX XXX XXX"
                        value={contactPersonPhone}
                        onChange={(e) => setContactPersonPhone(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact-person-position" className="text-[var(--navy)]">Job Title</Label>
                      <Input
                        id="contact-person-position"
                        type="text"
                        placeholder="e.g., Director of Innovation"
                        value={contactPersonPosition}
                        onChange={(e) => setContactPersonPosition(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6 space-y-4">
                  <h3 className="text-[var(--navy)] font-semibold">Requested Access</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="innovation-count" className="text-[var(--navy)]">How many innovations do you expect to submit annually? (slot request) *</Label>
                    <Select value={innovationCount} onValueChange={setInnovationCount} required>
                      <SelectTrigger id="innovation-count">
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent>
                        {innovationPricingOptions.map((option) => (
                          <SelectItem key={option.range} value={option.range}>
                            {option.label} - KSh {option.price}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {innovationCount && (
                      <p className="mt-2 text-sm text-[var(--navy)]/70">
                        Requested slots: {requestedSlotsFromRange[innovationCount] ?? 100}. Pricing is negotiated off-platform.
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setViewMode('select-role');
                      setSelectedRole(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={institutionApplicationSubmitting}
                  >
                    {institutionApplicationSubmitting ? 'Submitting...' : 'Submit Application'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Confirmation Screen */}
        {backendReady && viewMode === 'confirmation' && (
          <Card className="max-w-2xl mx-auto bg-white border-[rgba(201,168,76,0.25)] shadow-xl">
            <CardContent className="pt-12 pb-12 text-center">
              <div className="w-20 h-20 bg-[var(--gold-pale)] rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-[var(--gold)]" />
              </div>
              <h2 className="text-[var(--navy)] font-display font-semibold mb-4">
                {selectedRole === 'institution' ? 'Application Submitted!' : 'Registration Successful!'}
              </h2>
              <p className="text-[var(--navy)]/80 mb-4 max-w-lg mx-auto">
                {confirmationMessage}
              </p>
              {applicationReferenceNumber && (
                <p className="text-[var(--navy)] font-medium mb-6">Reference number: <strong>{applicationReferenceNumber}</strong></p>
              )}
              <Button
                onClick={handleBackToLogin}
                className=""
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}